import { useEffect, useRef } from "react";
const dialogs = [];
let originalOverflow;
// A single stack prevents Escape/Tab in a child dialog from acting on its parent.
export default function useDialog(ref, onClose) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const opener = document.activeElement;
    if (!dialogs.length) {
      originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    // React runs child effects before parent effects. Keep descendant dialogs
    // above their containing ModalPortal regardless of effect registration order.
    const childIndex = dialogs.findIndex(dialog => element.contains(dialog));
    if (childIndex >= 0) dialogs.splice(childIndex, 0, element);
    else dialogs.push(element);
    const controls = () => Array.from(element.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'))
      .filter(node => !node.closest('[hidden], [inert], [aria-hidden="true"]') && getComputedStyle(node).visibility !== "hidden" && getComputedStyle(node).display !== "none");
    const timer = setTimeout(() => {
      if (dialogs[dialogs.length - 1] === element) (controls()[0] || element).focus({ preventScroll: true });
    }, 0);
    const keydown = event => {
      if (dialogs[dialogs.length - 1] !== element || event.defaultPrevented) return;
      if (event.key === "Escape" && close.current) {
        event.preventDefault();
        event.stopPropagation();
        close.current();
      }
      if (event.key === "Tab") {
        const items = controls(), first = items[0], last = items[items.length - 1];
        if (!first || !element.contains(document.activeElement) ||
          (event.shiftKey && document.activeElement === first) ||
          (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last || element : first || element).focus();
        }
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", keydown);
      const wasTop = dialogs[dialogs.length - 1] === element;
      const index = dialogs.indexOf(element);
      if (index >= 0) dialogs.splice(index, 1);
      if (!dialogs.length) document.body.style.overflow = originalOverflow;
      if (wasTop && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [ref]);
}
